import { useCallback, useMemo, useState } from 'react';

import { LoadedData } from '@occc/utils';
import { ScanDataMetricsTimeSeriesFragment, TimeSeriesDataPointFragment } from '@occc/graphql';

import { createMetricsFromTimeSeriesData } from '../scan-data-overview.utils';
import { useScanDataMetricsTimeSeries } from '../../../hooks/use-scan-data-metrics-time-series.hook';
import { ScanDataMetric } from '../scan-data-overview.types';

export type UseScanDataMetricTabsDataReturn = {
  metricsData: ScanDataMetric[];
  selectedMetricIndex: number;
  selectedMetric: ScanDataMetric;
  handleMetricChange: (index: number) => void;
  scanDataMetricsTimeSeriesLoadedData: LoadedData<ScanDataMetricsTimeSeriesFragment>;
  selectedMetricChartData: TimeSeriesDataPointFragment[] | undefined;
  chartDataByMetricType: Record<string, TimeSeriesDataPointFragment[] | undefined>;
};

export const useScanDataMetricTabsData = (
  submissionDateEquals: string,
): UseScanDataMetricTabsDataReturn => {
  const scanDataMetricsTimeSeries = useScanDataMetricsTimeSeries(submissionDateEquals);

  const scanDataMetricsTimeSeriesLoadedData: LoadedData<ScanDataMetricsTimeSeriesFragment> =
    useMemo(() => {
      return {
        data: scanDataMetricsTimeSeries.data?.scanDataMetricsTimeSeries,
        loading: scanDataMetricsTimeSeries.loading,
        error: scanDataMetricsTimeSeries.error,
      };
    }, [
      scanDataMetricsTimeSeries.data?.scanDataMetricsTimeSeries,
      scanDataMetricsTimeSeries.loading,
      scanDataMetricsTimeSeries.error,
    ]);

  const metricsData = useMemo(() => {
    return createMetricsFromTimeSeriesData(scanDataMetricsTimeSeries);
  }, [scanDataMetricsTimeSeries]);

  const [selectedMetricIndex, setSelectedMetricIndex] = useState(0);

  const handleMetricChange = useCallback((index: number) => {
    setSelectedMetricIndex(index);
  }, []);

  const selectedMetric = metricsData[selectedMetricIndex];

  const chartDataByMetricType = useMemo(() => {
    const timeSeriesData = scanDataMetricsTimeSeries.data?.scanDataMetricsTimeSeries;

    if (!timeSeriesData) {
      return {};
    }

    return {
      'stores-submitted': timeSeriesData.storesSubmitted,
      'missing-stores': timeSeriesData.missingStores,
      'transactions-reported': timeSeriesData.transactionsReported,
      reimbursements: timeSeriesData.reimbursements,
    };
  }, [scanDataMetricsTimeSeries.data?.scanDataMetricsTimeSeries]);

  const selectedMetricChartData = selectedMetric
    ? chartDataByMetricType[selectedMetric.metricType]
    : undefined;

  return {
    metricsData,
    selectedMetricIndex,
    selectedMetric,
    handleMetricChange,
    scanDataMetricsTimeSeriesLoadedData,
    selectedMetricChartData,
    chartDataByMetricType,
  };
};
